import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import {
  BuildInfo,
  CURRENT_BUILD_INFO,
  CURRENT_BUILD_KEY,
  getBuildKey,
} from "./build-version";

@Injectable({
  providedIn: "root",
})
export class VersionCheckService {
  constructor(private http: HttpClient) {}

  /**
   * Checks the deployed build once and reloads the redirect URL when this
   * JavaScript bundle is older. Returns true when a reload was initiated.
   */
  async reloadIfNewVersion(): Promise<boolean> {
    console.info(
      `[Version check] Checking for updates. Current version: ${this.formatBuildInfo(CURRENT_BUILD_INFO)}.`,
    );

    try {
      const deployedBuild = await firstValueFrom(
        this.http.get<BuildInfo>("/assets/build-info.json", {
          params: { cacheBust: Date.now().toString() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        }),
      );
      const deployedBuildKey = getBuildKey(deployedBuild);
      console.info(
        `[Version check] Deployed version: ${this.formatBuildInfo(deployedBuild)}.`,
      );

      if (deployedBuildKey === CURRENT_BUILD_KEY) {
        console.info("[Version check] Application is up to date.");
        return false;
      }

      const redirectUrl = new URL(window.location.href);
      if (redirectUrl.searchParams.get("appVersion") === deployedBuildKey) {
        console.warn(
          "The latest application build could not be loaded after refresh.",
          { current: CURRENT_BUILD_INFO, deployed: deployedBuild },
        );
        return false;
      }

      console.info(
        `[Version check] Updating application from ${this.formatBuildInfo(CURRENT_BUILD_INFO)} to ${this.formatBuildInfo(deployedBuild)}.`,
      );
      redirectUrl.searchParams.set("appVersion", deployedBuildKey);
      this.replaceLocation(redirectUrl.toString());
      return true;
    } catch (error) {
      console.warn("Failed to check application build info.", error);
      return false;
    }
  }

  protected replaceLocation(url: string): void {
    window.location.replace(url);
  }

  private formatBuildInfo(buildInfo: BuildInfo): string {
    return `${buildInfo.buildNumber} (${buildInfo.buildDate})`;
  }
}
